import type { ToneNotation } from "@kensio/pinyinjs";

import type { CanonicalEntry } from "../segmenting/canonical-entry.js";
import { element } from "./dom.js";
import type { SegmentedWord } from "../segmenting/segmented-words.js";
import { wordRow } from "./segmenter-rows.js";

/**
 * The whole list, and the two groups it is read in.
 *
 * Apart from `segmenter-rows.ts`, which draws one row. A reader signed in and
 * holding some of these words wants to see which, and a row that says so per
 * row would say it a few hundred times. Two headings say it twice.
 */

/** The six columns, which a group heading has to span. */
const columns = 6;

/** A heading separating new words from words the reader already holds. */
function groupRow(label: string): HTMLTableRowElement {
  return element("tr", {
    className: "cb-segmenter-group",
    children: [
      element("th", {
        attributes: { colspan: String(columns), scope: "rowgroup" },
        text: label,
      }),
    ],
  });
}

/** What the whole list needs told. */
export interface WordListProperties {
  readonly words: readonly SegmentedWord[];

  readonly notation: ToneNotation;

  /** The words the reader holds, or nothing where the page cannot say. */
  readonly held?: ReadonlySet<string> | undefined;

  /** What the site says a word means, where this page has been told. */
  readonly entryOf?:
    | ((headword: string) => CanonicalEntry | undefined)
    | undefined;

  /** The rows whose meaning the reader has opened. */
  readonly open?: ReadonlySet<string> | undefined;

  /** Where the icon sprite is, for the marks the save control draws. */
  readonly icons?: string | undefined;
}

/** The whole list as rows, ready to go into the table body. */
export function wordRows(properties: WordListProperties): readonly Node[] {
  const { words, held } = properties;
  const row = (word: SegmentedWord, isHeld: boolean): HTMLTableRowElement => {
    const headword = word.headword ?? "";

    return wordRow({
      word,
      notation: properties.notation,
      held: isHeld,
      entry: properties.entryOf?.(headword),
      open: properties.open?.has(headword),
      icons: properties.icons,
    });
  };

  if (held === undefined) {
    return words.map((word) => row(word, false));
  }

  const fresh: SegmentedWord[] = [];
  const saved: SegmentedWord[] = [];

  for (const word of words) {
    (word.headword !== undefined && held.has(word.headword)
      ? saved
      : fresh
    ).push(word);
  }

  return [
    ...(fresh.length === 0
      ? []
      : [groupRow("New words 生词"), ...fresh.map((word) => row(word, false))]),
    ...(saved.length === 0
      ? []
      : [
          groupRow("Saved words 已收藏词"),
          ...saved.map((word) => row(word, true)),
        ]),
  ];
}
